import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api, resolveImageUrl } from '../lib/api';
import { Button } from './ui/button';
import { toast } from 'sonner';
import {
  LayoutDashboard, PenTool, Users, CheckCircle, Settings, LogOut,
  Menu, X, DollarSign, BarChart3, Puzzle, QrCode, MessageSquare,
  Bell, MessagesSquare, Calendar, BookOpen, Search, UserPlus,
  ChevronDown, Briefcase, Network, ShoppingBag, User, CreditCard, Award, Compass,
  Trophy,
} from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import FeedbackModal from './FeedbackModal';
import { Logo } from './Logo';

// Navigation structure with dropdown groups - UPDATED
const NAV_STRUCTURE = [
  { type: 'link', href: '/dashboard', label: 'Home', icon: LayoutDashboard },
  { type: 'link', href: '/network', label: 'Connections', icon: Network },
  { type: 'link', href: '/explore', label: 'Explore', icon: Compass },
  { type: 'link', href: '/signatures', label: 'Signatures', icon: PenTool },
  { type: 'link', href: '/groups', label: 'Groups', icon: Users },
  { type: 'link', href: '/events', label: 'Events', icon: Calendar },
  { type: 'link', href: '/business', label: 'Business', icon: Briefcase },
];

// Flat list for mobile nav
const ALL_NAV_ITEMS = NAV_STRUCTURE.flatMap(n =>
  n.type === 'group' ? n.items : [{ href: n.href, label: n.label, icon: n.icon }]
);

function NavDropdown({ group, location, onNavigate, isDark }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const isActive = group.items.some(i => location.pathname === i.href || location.pathname.startsWith(i.href.split('?')[0]));

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(o => !o)}
        className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-extrabold transition-colors ${
          isActive 
            ? 'bg-white/20 text-white' 
            : isDark 
              ? 'text-white/80 hover:text-white hover:bg-white/10' 
              : 'text-muted-foreground hover:text-foreground hover:bg-muted'
        }`}
        data-testid={`nav-group-${group.label.toLowerCase().replace(' ', '-')}`}
      >
        <group.icon className="w-4 h-4" />
        {group.label}
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 w-60 bg-background border border-border rounded-xl shadow-xl z-50 py-2 overflow-hidden">
          {group.items.map(item => {
            const Icon = item.icon;
            const itemActive = location.pathname === item.href || location.pathname.startsWith(item.href.split('?')[0]);
            return (
              <Link
                key={item.href}
                to={item.href}
                onClick={() => { setOpen(false); onNavigate?.(); }}
                className={`flex items-start gap-3 px-4 py-2.5 hover:bg-muted/60 transition-colors ${itemActive ? 'bg-primary/10' : ''}`}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${itemActive ? 'text-primary' : 'text-muted-foreground'}`} />
                <div>
                  <p className={`text-sm font-semibold ${itemActive ? 'text-primary' : ''}`}>{item.label}</p>
                  {item.desc && <p className="text-xs text-muted-foreground">{item.desc}</p>}
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Profile Dropdown Component
function ProfileDropdown({ user, logout, isDark }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const menuItems = [
    { href: '/card/me', label: 'My Card', icon: User, desc: 'View your business card' },
    { href: '/leaderboard', label: 'Leaderboard', icon: Trophy, desc: 'Rankings & badges' },
    ...(user?.account_type === 'business' ? [{ href: '/hiring', label: 'Hiring', icon: Briefcase, desc: 'Manage job posts', badge: user?.is_hiring }] : []),
    { href: '/settings', label: 'My Profile', icon: Settings, desc: 'Account preferences' },
  ];

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(o => !o)}
        className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] font-medium transition-colors ${
          isDark ? 'text-white/80 hover:text-white hover:bg-white/10' : 'text-muted-foreground hover:text-foreground hover:bg-muted'
        }`}
        data-testid="profile-dropdown-trigger"
      >
        <div className="w-6 h-6 rounded-full bg-gradient-to-br from-primary/60 to-primary flex items-center justify-center overflow-hidden border border-white/20">
          {user?.profile_photo_url ? (
            <img src={resolveImageUrl(user.profile_photo_url)} alt="" className="w-full h-full object-cover" />
          ) : (
            <span className="text-white text-[10px] font-bold">{(user?.full_name || 'U')[0]}</span>
          )}
        </div>
        <span className="hidden sm:inline">{user?.full_name?.split(' ')[0] || 'Profile'}</span>
        <ChevronDown className={`w-2.5 h-2.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-56 bg-background border border-border rounded-lg shadow-xl z-50 py-1 overflow-hidden" data-testid="profile-dropdown-menu">
          {/* User Info Header */}
          <div className="px-3 py-2 border-b border-border/50">
            <p className="text-sm font-semibold truncate">{user?.full_name}</p>
            <p className="text-[10px] text-muted-foreground truncate">{user?.email}</p>
            {user?.is_premium && (
              <span className="inline-flex items-center gap-1 text-[9px] text-green-600 font-medium mt-1">
                <Award className="w-2.5 h-2.5" />
                Premium Member
              </span>
            )}
          </div>
          
          {/* Menu Items */}
          {menuItems.map(item => {
            const Icon = item.icon;
            return (
              <Link
                key={item.href}
                to={item.href}
                onClick={() => setOpen(false)}
                className="flex items-start gap-2.5 px-3 py-2 hover:bg-muted/60 transition-colors"
                data-testid={`profile-menu-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                <Icon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-muted-foreground" />
                <div>
                  <div className="flex items-center gap-1">
                    <p className="text-xs font-medium">{item.label}</p>
                    {item.badge && <span className="w-1.5 h-1.5 rounded-full bg-green-500" />}
                  </div>
                  {item.desc && <p className="text-[10px] text-muted-foreground">{item.desc}</p>}
                </div>
              </Link>
            );
          })}
          
          {/* Logout */}
          <div className="border-t border-border/50 mt-1 pt-1">
            <button
              onClick={() => { setOpen(false); logout(); }}
              className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-muted/60 transition-colors text-left"
              data-testid="profile-logout-btn"
            >
              <LogOut className="w-3.5 h-3.5 text-muted-foreground" />
              <span className="text-xs font-medium">Logout</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifs, setShowNotifs] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const notifRef = useRef(null);

  useEffect(() => {
    const fetchUnread = async () => {
      try { const d = await api.getUnreadCount(); setUnreadCount(d.unread_count || 0); } catch {}
    };
    fetchUnread();
    const t = setInterval(fetchUnread, 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const h = (e) => { if (notifRef.current && !notifRef.current.contains(e.target)) setShowNotifs(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const openNotifs = async () => {
    setShowNotifs(o => !o);
    if (!showNotifs) {
      try { const d = await api.getNotifications(); setNotifications(d.notifications || []); } catch {}
    }
  };

  const markAllRead = async () => {
    try {
      await api.markAllNotificationsRead();
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch {}
  };

  return (
    <div className="min-h-screen bg-muted/30" data-testid="app-layout">
      {/* Header - Professional dark blue, THICKER */}
      <header className="sticky top-0 z-50 bg-[#1e3a5f] border-b border-[#2d4a6f]">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14 sm:h-20">
            {/* Logo */}
            <Link to="/dashboard" className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0 min-w-0" data-testid="logo-link">
              <img src="/brand/logo-icon.png" alt="ConnectMail" className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-white p-0.5 flex-shrink-0" />
              <span className="font-extrabold text-base sm:text-xl tracking-tight font-['Outfit'] text-white truncate">ConnectMail</span>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden lg:flex items-center gap-1">
              {NAV_STRUCTURE.map((item, idx) => {
                if (item.type === 'group') {
                  return <NavDropdown key={idx} group={item} location={location} isDark />;
                }
                const Icon = item.icon;
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-extrabold transition-colors ${
                      isActive 
                        ? 'bg-white/20 text-white' 
                        : 'text-white/80 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    {item.label}
                  </Link>
                );
              })}
            </nav>

            {/* Right side */}
            <div className="flex items-center gap-2">
              {user?.is_premium && (
                <Link to="/dashboard" className="hidden sm:flex items-center gap-1 text-xs" data-testid="earnings-display">
                  <DollarSign className="w-4 h-4 text-green-400" />
                  <span className="font-bold text-green-400">${user?.total_earnings?.toFixed(2) || '0.00'}</span>
                </Link>
              )}

              {/* Notification Bell */}
              <div className="relative" ref={notifRef}>
                <Button variant="ghost" size="sm" onClick={openNotifs}
                  data-testid="notification-bell"
                  className={`hover:bg-white/10 h-9 px-2 relative ${unreadCount > 0 ? 'text-red-400 hover:text-red-300' : 'text-white/80 hover:text-white'}`} title="Notifications">
                  <Bell className={`w-4 h-4 ${unreadCount > 0 ? 'animate-[pulse_2s_ease-in-out_infinite]' : ''}`} />
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[9px] rounded-full w-3.5 h-3.5 flex items-center justify-center font-bold" data-testid="unread-badge">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </Button>
                {showNotifs && (
                  <div className="absolute right-0 top-full mt-1 w-80 bg-background border border-border rounded-lg shadow-xl z-50 max-h-96 overflow-auto" data-testid="notification-dropdown">
                    <div className="flex items-center justify-between p-3 border-b border-border">
                      <span className="text-sm font-semibold">Notifications</span>
                      {unreadCount > 0 && <button onClick={markAllRead} className="text-xs text-primary hover:underline" data-testid="mark-all-read">Mark all read</button>}
                    </div>
                    {notifications.length === 0 ? (
                      <p className="p-4 text-sm text-muted-foreground text-center">No notifications yet</p>
                    ) : notifications.slice(0, 20).map(n => (
                      <Link key={n.id} to={n.link || '/dashboard'}
                        onClick={() => { setShowNotifs(false); if (!n.read) api.markNotificationRead(n.id).then(() => setUnreadCount(c => Math.max(0, c - 1))); }}
                        className={`block p-3 border-b border-border/50 hover:bg-muted/50 transition-colors ${!n.read ? 'bg-primary/5' : ''}`}
                        data-testid={`notif-${n.id}`}
                      >
                        <p className="text-sm font-medium truncate">{n.title}</p>
                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.message}</p>
                        <p className="text-[10px] text-muted-foreground/60 mt-1">{new Date(n.created_at).toLocaleDateString()}</p>
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              <Button variant="ghost" size="sm" onClick={() => setFeedbackOpen(true)}
                data-testid="feedback-btn"
                className="text-white/80 hover:text-white hover:bg-white/10 h-9 px-2" title="Send Feedback">
                <MessageSquare className="w-4 h-4" />
                <span className="hidden lg:inline ml-1.5 text-xs font-bold">Feedback</span>
              </Button>

              {/* Profile Dropdown */}
              <ProfileDropdown user={user} logout={logout} isDark />

              <Button variant="ghost" size="icon" className="lg:hidden h-9 w-9 text-white/80 hover:text-white hover:bg-white/10"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-btn">
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile Nav */}
        {mobileMenuOpen && (
          <nav className="lg:hidden border-t border-border/50 bg-background max-h-[70vh] overflow-y-auto">
            <div className="px-4 py-3 space-y-1">
              {ALL_NAV_ITEMS.map(item => {
                const Icon = item.icon;
                const isActive = location.pathname === item.href;
                return (
                  <Link key={item.href} to={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                      isActive ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {item.label}
                  </Link>
                );
              })}
              <button onClick={() => { setMobileMenuOpen(false); setFeedbackOpen(true); }}
                className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors text-muted-foreground hover:text-foreground hover:bg-muted w-full"
                data-testid="mobile-feedback-btn">
                <MessageSquare className="w-4 h-4" />Send Feedback
              </button>
            </div>
          </nav>
        )}
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8 pb-24 md:pb-8">
        {children}
      </main>

      <FeedbackModal open={feedbackOpen} onOpenChange={setFeedbackOpen} />
    </div>
  );
};

export default Layout;
